import {
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { EMPTY, Subject, catchError, of, switchMap, tap } from 'rxjs';

import { AuthService } from '../../../core/services/auth/auth.service';
import { SortDirectionType } from '../../../core/models/sort-direction';
import { Dropdown, DropdownOption } from '../../../shared/components/dropdown/dropdown';
import { IconButtonComponent } from '../../../shared/components/icon-button/icon-button.component';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { InputComponent } from '../../../shared/components/input/input.component';
import { ProgressSpinnerComponent } from '../../../shared/components/progress-spinner/progress-spinner.component';
import { PageItem, getPageItems } from '../../../shared/utils/pagination/pagination.utils';
import {
  formatDobNumeric,
  formatRelativeTime,
  parseUtc,
} from '../../../shared/utils/date-time/date-time.utils';
import {
  RequestStatusKind,
  getStatusBadge,
} from '../../../shared/utils/status-badge/status-badge.utils';
import { EMPTY_CELL, buildQueueRowAriaLabel } from '../../../shared/utils/table/table.utils';
import { PatientPortalUserRequest } from '../../models/patient-portal-user-request';
import { WorkflowStatusType } from '../../../core/models/workflow-status-type';
import {
  AssignedToFilter,
  NewLinkingRequestsFilterStateService,
} from '../../services/new-linking-requests-filter-state.service';
import {
  PatientPortalUserRequestsRequest,
  PatientPortalUserRequestsResult,
  PatientPortalUserRequestsService,
} from '../../services/patient-portal-user-requests.service';
import { getIdentifierAriaLabel, getVisibleIdentifier } from '../../utils/identifier.utils';

interface LinkingRequest {
  id: number;
  submittedAt: Date;
  patientName: string;
  dob: string;
  identifier: string;
  identifierAriaLabel: string;
  ariaLabel: string;
  assignedToDisplayName: string | null;
  statusLabel: string;
  statusKind: RequestStatusKind;
}

/** `null` is the explicit "All Time" row — see the Dropdown value contract. */
type DateRangeOption = DropdownOption<number | null>;
type StatusFilterOption = DropdownOption<WorkflowStatusType | null>;
type AssignedToFilterOption = DropdownOption<AssignedToFilter>;

@Component({
  selector: 'app-new-linking-requests',
  standalone: true,
  imports: [
    Dropdown,
    FormsModule,
    IconComponent,
    IconButtonComponent,
    InputComponent,
    ProgressSpinnerComponent,
  ],
  templateUrl: './new-linking-requests.html',
  styleUrls: ['./new-linking-requests.scss'],
})
export class NewLinkingRequests implements OnInit {
  /**
   * Rendered height of a single table row (see .queue-table__row in the scss):
   * 14px padding top/bottom + 22px badge chip + 1px border.
   */
  private static readonly ROW_HEIGHT_PX = 51;
  /**
   * Fixed chrome inside .table-shell that the rows do not get: the column
   * header (9px padding top/bottom + 14px line + 1.5px --bw-thick border ≈ 34px)
   * plus the pagination footer (12px padding top/bottom + a 32px page button +
   * 1px border = 57px). Subtracted from the shell rather than measuring the row
   * container directly, because the shell is the one element that exists in
   * every state, and the first load waits on this measurement (see the
   * measurement effect) — measuring a container that only exists once rows
   * render would leave the queue with nothing to measure and never load. Keep
   * in sync with the header/pagination mixins in styles/partials/_queue-table.scss.
   */
  private static readonly TABLE_CHROME_PX = 91;
  /** Page size used before the table has been measured, and the floor afterwards. */
  private static readonly MIN_PAGE_SIZE = 6;
  /** Minimum characters before a search term is sent to the API. */
  private static readonly MIN_SEARCH_LENGTH = 3;

  private readonly destroyRef = inject(DestroyRef);
  private readonly requestsService = inject(PatientPortalUserRequestsService);
  private readonly filterState = inject(NewLinkingRequestsFilterStateService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly loadRequestsTrigger$ = new Subject<void>();
  private readonly tableShell = viewChild<ElementRef<HTMLElement>>('tableShell');
  private resizeTimer?: ReturnType<typeof setTimeout>;
  /**
   * Whether the queue has been requested yet this visit. The first request is
   * deliberately deferred until the shell has been measured, so the queue is
   * fetched exactly once per visit — see recalculatePageSize.
   */
  private hasLoadedOnce = false;

  readonly pageSize = signal(NewLinkingRequests.MIN_PAGE_SIZE);
  readonly dateRangeOptions: DateRangeOption[] = [
    { label: 'All Time', value: null },
    {
      label: 'Last 7 Days',
      value: NewLinkingRequestsFilterStateService.DEFAULT_DATE_RANGE_DAYS,
    },
    { label: 'Last 30 Days', value: 30 },
    { label: 'Last 90 Days', value: 90 },
    { label: 'Last 12 Months', value: 365 },
  ];
  readonly statusFilterOptions: StatusFilterOption[] = [
    { label: 'All', value: null },
    { label: 'New', value: WorkflowStatusType.New },
    { label: 'Needs Info', value: WorkflowStatusType.NeedsInfo },
    { label: 'In Progress', value: WorkflowStatusType.InProgress },
  ];

  readonly isLoading = signal(true);
  // Filters are backed by a root-provided service so they stay sticky across
  // in-app navigation (e.g. opening a request and coming back), rather than
  // resetting each time this component is re-created. The dropdowns apply (and
  // reload the queue) as soon as they change; only the search term waits for
  // the Filter button.
  readonly selectedStatus = this.filterState.statusFilter;
  readonly selectedAssignedTo = this.filterState.assignedToFilter;
  readonly selectedDateRange = this.filterState.dateRangeDays;
  readonly searchTerm = this.filterState.searchTerm;
  private readonly appliedSearchTerm = this.filterState.appliedSearchTerm;
  /** Admin user id backing the "Me" filter; null until the user is resolved. */
  readonly currentUserId = computed(
    () => this.authService.currentUser()?.patientPortalAdminUserId ?? null,
  );
  /**
   * Computed rather than a constant because "Me" is only selectable once the
   * current user's admin id resolves: choosing it without an id would send an
   * id-less payload and return the whole queue under a filter that says "Me".
   * The option still renders (disabled) so the filter's vocabulary is stable.
   */
  readonly assignedToFilterOptions = computed<AssignedToFilterOption[]>(() => [
    { label: 'All', value: 'all' },
    { label: 'Me', value: 'me', disabled: this.currentUserId() === null },
    { label: 'Unassigned', value: 'unassigned' },
  ]);
  readonly currentPage = signal(1);
  readonly selectedRequestId = signal<number | null>(null);
  readonly allRequests = signal<LinkingRequest[]>([]);
  readonly totalRequests = signal(0);
  readonly loadError = signal<string | null>(null);
  readonly hasActiveSearch = computed(() => this.appliedSearchTerm() !== null);

  /** True when any queue filter narrows the results (status, assignee, or search). */
  readonly hasActiveFilters = computed(
    () =>
      this.selectedStatus() !== null ||
      this.selectedAssignedTo() !== 'all' ||
      this.hasActiveSearch(),
  );

  /**
   * True when the typed term is non-empty but normalizes below the search
   * threshold. Applying such a term would silently reload the unfiltered
   * queue under visible search text, so applyFilters rejects it.
   */
  private readonly isSearchTermTooShort = computed(
    () => this.searchTerm().trim().length > 0 && this.getNormalizedSearchTerm() === null,
  );

  /**
   * Validation message for the search box. Only set when the user tries to
   * apply a too-short term (Filter click or Enter) — never while they are
   * still typing — and cleared again as soon as the term changes.
   */
  readonly searchValidationError = signal<string | null>(null);

  readonly totalPages = computed(() => {
    const total = this.totalRequests();
    return total === 0 ? 1 : Math.ceil(total / this.pageSize());
  });

  readonly pageItems = computed<PageItem[]>(() =>
    getPageItems(this.currentPage(), this.totalPages()),
  );

  readonly showingSummary = computed(() => {
    const total = this.totalRequests();

    if (total === 0) {
      return 'Showing 0 of 0 requests';
    }

    const start = (this.currentPage() - 1) * this.pageSize() + 1;
    const end = Math.min(start + this.allRequests().length - 1, total);
    return `Showing ${start}\u2013${end} of ${total} requests`;
  });

  constructor() {
    // Grow the page size to fill the available table height so larger screens
    // show more rows per page. The shell flexes to fill the viewport and is
    // rendered in every state (loading, error, empty, populated), so it can be
    // measured before any rows exist. This effect owns the queue's first load
    // (recalculatePageSize issues it) rather than ngOnInit: the measurement can
    // only run once the view exists, so loading in ngOnInit would fire one
    // PHI-bearing request at the fallback size and a second at the measured
    // size on every visit.
    effect((onCleanup) => {
      const element = this.tableShell()?.nativeElement;
      if (!element) {
        return;
      }

      this.recalculatePageSize(element);

      if (typeof ResizeObserver === 'undefined') {
        return;
      }

      const observer = new ResizeObserver(() => this.schedulePageSizeRecalc(element));
      observer.observe(element);
      onCleanup(() => {
        observer.disconnect();
        clearTimeout(this.resizeTimer);
      });
    });
  }

  ngOnInit(): void {
    this.loadRequestsTrigger$
      .pipe(
        tap(() => {
          this.isLoading.set(true);
          this.loadError.set(null);
        }),
        switchMap(() => {
          // Fail closed: if the sticky filter still says "Me" but the current
          // user's admin id is unknown, an id-less payload would return the
          // unfiltered queue while the UI says "Me" — show no results instead.
          if (this.isAssignedToMeUnresolved()) {
            return of<PatientPortalUserRequestsResult>({ requests: [], totalRecordCount: 0 });
          }

          return this.requestsService.getRequests(this.buildRequestsPayload()).pipe(
            catchError(() => {
              this.loadError.set('Unable to load linking requests right now.');
              this.allRequests.set([]);
              this.totalRequests.set(0);
              this.isLoading.set(false);
              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result) => {
          this.applyRequestsResult(result);
          this.loadError.set(null);
          this.isLoading.set(false);
        },
      });

    // No load here on purpose: the measurement effect issues the first request
    // once the shell has been measured, so the queue is fetched once per visit
    // at the right page size. isLoading already starts true, so the spinner is
    // on screen from the first render until that request resolves.
  }

  onStatusChange(value: WorkflowStatusType | null): void {
    this.selectedStatus.set(value);
    this.reloadFromFirstPage();
  }

  onAssignedToChange(value: AssignedToFilter): void {
    this.selectedAssignedTo.set(value);
    this.reloadFromFirstPage();
  }

  onDateRangeChange(value: number | null): void {
    this.selectedDateRange.set(value);
    this.reloadFromFirstPage();
  }

  /**
   * Commit the current search term and reload the queue from page 1 (Filter
   * button or Enter in the search box). A too-short term surfaces a
   * validation error instead of silently reloading the unfiltered queue.
   */
  applyFilters(): void {
    if (this.isSearchTermTooShort()) {
      this.searchValidationError.set('Enter at least 3 characters to search.');
      return;
    }

    this.searchValidationError.set(null);
    this.appliedSearchTerm.set(this.getNormalizedSearchTerm());
    this.reloadFromFirstPage();
  }

  /** Typing in the search box never reloads the queue — the Filter button applies it. */
  onSearchChange(value: string): void {
    this.searchTerm.set(value);
    // The error describes a rejected apply attempt; editing the term starts a
    // new one, so don't nag while the user is still typing.
    this.searchValidationError.set(null);
  }

  clearSearch(): void {
    this.searchValidationError.set(null);

    if (!this.searchTerm()) {
      return;
    }

    this.searchTerm.set('');

    // Clicking the X clears the box; reload the unfiltered list when a search
    // was applied.
    if (this.appliedSearchTerm() !== null) {
      this.appliedSearchTerm.set(null);
      this.reloadFromFirstPage();
    }
  }

  goToPage(page: number): void {
    const nextPage = Math.max(1, Math.min(page, this.totalPages()));
    if (nextPage === this.currentPage()) {
      return;
    }

    this.currentPage.set(nextPage);
    this.loadRequests();
  }

  openRequest(requestId: number): void {
    this.selectedRequestId.set(requestId);
    void this.router.navigate(['/request-detail', requestId]);
  }

  onRowKeydown(event: KeyboardEvent, requestId: number): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.openRequest(requestId);
    }
  }

  isRowSelected(requestId: number): boolean {
    return this.selectedRequestId() === requestId;
  }

  getRelativeSubmittedAt(request: LinkingRequest): string {
    return formatRelativeTime(request.submittedAt);
  }

  private mapRequest(request: PatientPortalUserRequest): LinkingRequest {
    const badge = getStatusBadge(request.workflowStatusTypeId);
    const dob = formatDobNumeric(request.dateOfBirth);
    const identifierAriaLabel = getIdentifierAriaLabel(request);

    return {
      id: request.patientPortalUserRequestId,
      // Keep the true instant: submittedAt only feeds the "X hrs ago" elapsed
      // calculation, which must measure real time difference, not wall-clock.
      submittedAt: parseUtc(request.createdOnUtc),
      patientName: request.patientDisplayName ?? EMPTY_CELL,
      dob,
      identifier: getVisibleIdentifier(request),
      identifierAriaLabel,
      // The row's announcement must carry the columns, not just the name — see
      // buildQueueRowAriaLabel. A request with no submitted name drops the name
      // clause instead of reading out the cell's em-dash placeholder.
      ariaLabel: buildQueueRowAriaLabel(
        request.patientDisplayName
          ? `Open request for ${request.patientDisplayName}`
          : 'Open request',
        [
          dob ? `date of birth ${dob}` : null,
          identifierAriaLabel,
          request.assignedToDisplayName
            ? `assigned to ${request.assignedToDisplayName}`
            : 'unassigned',
          `status ${badge.label}`,
        ],
      ),
      assignedToDisplayName: request.assignedToDisplayName,
      statusLabel: badge.label,
      statusKind: badge.kind,
    };
  }

  /** True when the "Me" assignee filter is active but the admin user id hasn't resolved. */
  private isAssignedToMeUnresolved(): boolean {
    return this.selectedAssignedTo() === 'me' && this.currentUserId() === null;
  }

  private reloadFromFirstPage(): void {
    this.currentPage.set(1);
    this.loadRequests();
  }

  private loadRequests(): void {
    this.hasLoadedOnce = true;
    this.loadRequestsTrigger$.next();
  }

  private schedulePageSizeRecalc(element: HTMLElement): void {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => this.recalculatePageSize(element), 120);
  }

  /**
   * Size the page to the measured shell and load the queue when that changed
   * anything. Also owns the first load of the visit: an element that measures 0
   * (detached, hidden, or a jsdom test) falls back to MIN_PAGE_SIZE rather than
   * leaving the queue unfetched.
   */
  private recalculatePageSize(element: HTMLElement): void {
    const availableHeight = element.clientHeight - NewLinkingRequests.TABLE_CHROME_PX;
    const rowsThatFit =
      availableHeight > 0 ? Math.floor(availableHeight / NewLinkingRequests.ROW_HEIGHT_PX) : 0;
    const nextPageSize = Math.max(NewLinkingRequests.MIN_PAGE_SIZE, rowsThatFit);

    // Read untracked: this runs inside the measurement effect, and tracking
    // pageSize there would make the effect re-run (tearing down and re-creating
    // the ResizeObserver) every time it writes a new size.
    if (nextPageSize === untracked(this.pageSize)) {
      // The size is already right, so a resize has nothing to do — but the very
      // first measurement still owes the visit its one request.
      if (!this.hasLoadedOnce) {
        this.loadRequests();
      }
      return;
    }

    this.pageSize.set(nextPageSize);
    this.currentPage.set(1);
    this.loadRequests();
  }

  /**
   * Lower bound for the request date, or null for "All Time". The API treats a
   * null bound as unbounded, so the option needs no separate flag.
   */
  private getCreatedAfterUtc(): string | null {
    const days = this.selectedDateRange();
    if (days === null) {
      return null;
    }

    const createdAfter = new Date();
    createdAfter.setDate(createdAfter.getDate() - days);
    return createdAfter.toISOString();
  }

  private buildRequestsPayload(): PatientPortalUserRequestsRequest {
    const assignedTo = this.selectedAssignedTo();

    return {
      createdAfterUtc: this.getCreatedAfterUtc(),
      workflowStatusTypeId: this.selectedStatus(),
      assignedToPatientPortalAdminUserId: assignedTo === 'me' ? this.currentUserId() : null,
      unassignedOnly: assignedTo === 'unassigned',
      pageNumber: this.currentPage(),
      pageSize: this.pageSize(),
      searchTerm: this.appliedSearchTerm(),
      sortColumn: 'CreatedOnUtc',
      sortDirection: SortDirectionType.Descending,
    };
  }

  private applyRequestsResult(result: PatientPortalUserRequestsResult): void {
    this.allRequests.set(result.requests.map((request) => this.mapRequest(request)));
    this.totalRequests.set(result.totalRecordCount);
  }

  private getNormalizedSearchTerm(): string | null {
    const normalizedSearchTerm = this.searchTerm().trim().replace(/\b0+/g, '');
    return normalizedSearchTerm.length >= NewLinkingRequests.MIN_SEARCH_LENGTH
      ? normalizedSearchTerm
      : null;
  }
}
